import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import Header from '../components/Header';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { Magnetic } from '../components/motion/Magnetic';
import SEOHead, { breadcrumbSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to validate logo URLs
const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || 
         url.includes('amazonaws.com') || 
         url.includes('crystalmedia') ||
         url.startsWith('/');
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, postsRes, featuredRes, categoriesRes] = await Promise.all([
          fetch(`${API_URL}/api/content/settings`),
          fetch(`${API_URL}/api/blog/posts${selectedCategory ? `?category=${selectedCategory}` : ''}`),
          fetch(`${API_URL}/api/blog/posts/featured`),
          fetch(`${API_URL}/api/blog/categories`),
        ]);

        // Trigger scheduled post publishing check
        fetch(`${API_URL}/api/blog/check-scheduled`).catch(() => {});

        const settingsData = await settingsRes.json();
        const postsData = await postsRes.json();
        const featuredData = await featuredRes.json();
        const categoriesData = await categoriesRes.json();

        setSettings(settingsData);
        setPosts(postsData.posts || []);
        setFeaturedPosts(featuredData || []);
        setCategories(categoriesData || []);

        const photoLogo = settingsData.photo_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(photoLogo) ? photoLogo : '');
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SEOHead
        title="Wedding Blog — Tips, Inspiration & Real Weddings"
        description="Read Crystal Media's wedding blog for expert tips, real Pakistani wedding highlights, Indian wedding inspiration, Punjabi wedding stories, and luxury wedding planning advice for couples across Toronto, Vancouver, Montreal, and beyond."
        url="/blog"
        type="blog"
        keywords={['Wedding Blog', 'Wedding Planning Tips', 'Real Wedding Stories', 'South Asian Wedding Blog']}
        jsonLd={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }])}
      />
      {/* Header */}
      <Header logoUrl={logoUrl} variant="light" />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal><p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-4">Stories & Insights</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-presti text-4xl md:text-5xl lg:text-6xl text-black mb-6">Blog</h1></Reveal>
          <Reveal delay={0.16}>
            <p className="text-black/50 text-lg max-w-2xl mx-auto leading-relaxed">
              Stories of love, behind-the-scenes glimpses, and insights into the art of capturing your most precious moments.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Categories Filter */}
      {categories.length > 0 && (
        <section className="pb-12 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-5 py-2 text-xs tracking-[0.15em] uppercase border rounded-full transition-all ${
                  !selectedCategory 
                    ? 'bg-black text-white border-black' 
                    : 'border-black/20 text-black/50 hover:border-black/40'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={`px-5 py-2 text-xs tracking-[0.15em] uppercase border rounded-full transition-all ${
                    selectedCategory === cat.slug
                      ? 'bg-black text-white border-black' 
                      : 'border-black/20 text-black/50 hover:border-black/40'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Post */}
      {featuredPosts.length > 0 && !selectedCategory && (
        <section className="pb-16 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <Link 
              to={`/blog/${featuredPosts[0].slug}`}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-500"
              data-testid="featured-post"
            >
              <div className="aspect-[4/3] lg:aspect-auto overflow-hidden">
                {featuredPosts[0].featured_image_url ? (
                  <img 
                    src={featuredPosts[0].featured_image_url} 
                    alt={featuredPosts[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <span className="text-black/20 text-sm tracking-widest uppercase">Featured</span>
                  </div>
                )}
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <span className="text-[10px] tracking-[0.2em] uppercase text-black/30 mb-4">Featured Story</span>
                <h2 className="font-presti text-2xl md:text-3xl text-black mb-4 group-hover:opacity-70 transition-opacity">
                  {featuredPosts[0].title}
                </h2>
                <p className="text-black/50 leading-relaxed mb-6 line-clamp-3">
                  {featuredPosts[0].excerpt}
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-xs text-black/40">
                    <Calendar size={14} />
                    <span>{formatDate(featuredPosts[0].published_at)}</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className="pb-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-black/30 animate-pulse">Loading posts...</p>
            </div>
          ) : posts.length > 0 ? (
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <StaggerItem key={post.id} y={36}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    {post.featured_image_url ? (
                      <img 
                        src={post.featured_image_url} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <span className="text-black/20 text-xs tracking-widest uppercase">No Image</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    {post.categories && post.categories.length > 0 && (
                      <span className="text-[10px] tracking-[0.2em] uppercase text-black/30 mb-2 block">
                        {post.categories[0]}
                      </span>
                    )}
                    <h3 className="font-presti text-lg text-black mb-2 group-hover:opacity-70 transition-opacity line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-black/50 text-sm leading-relaxed mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-black/30">{formatDate(post.published_at)}</span>
                      <span className="text-xs tracking-[0.1em] uppercase text-black/40 group-hover:text-black flex items-center gap-1 transition-colors">
                        Read <ArrowRight size={12} />
                      </span>
                    </div>
                  </div>
                </Link>
                </StaggerItem>
              ))}
            </Stagger>
          ) : (
            <div className="text-center py-20">
              <p className="text-black/30 text-sm tracking-widest uppercase">No blog posts yet</p>
              <p className="text-black/20 text-xs mt-2">Check back soon for stories and insights</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-white border-t border-black/5">
        <Reveal className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-presti text-2xl md:text-3xl text-black mb-4">
            Stay Connected
          </h2>
          <p className="text-black/50 mb-8">
            Follow us on social media for the latest stories and behind-the-scenes content.
          </p>
          <div className="flex justify-center gap-4">
            <a 
              href={`https://instagram.com/${settings?.instagram?.replace('@', '') || 'crystalmedia.ca'}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 border border-black/20 text-black text-xs tracking-[0.15em] uppercase hover:bg-black hover:text-white transition-all"
            >
              Instagram
            </a>
            <Magnetic>
            <Link
              to="/inquire"
              className="px-6 py-3 bg-black text-white text-xs tracking-[0.15em] uppercase hover:bg-black/80 transition-colors"
            >
              Contact Us
            </Link>
            </Magnetic>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-black/5 bg-[#fafafa]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {logoUrl && !logoError ? (
            <img src={logoUrl} alt="Crystal Media" className="h-6 w-auto opacity-50" onError={() => setLogoError(true)} />
          ) : (
            <span className="font-presti text-xs tracking-[0.2em] text-black/40">CRYSTAL MEDIA</span>
          )}
          <p className="text-[10px] tracking-[0.15em] uppercase text-black/30">
            © 2025 Crystal Media. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
